import {
  applyRulebookAttachmentChange,
  CHARACTER_MODERATION_STATUS,
  CHARACTER_VISIBILITY,
  CharacterFlow,
  checkShouldBypassModerationReview,
  ICharacter,
  IUpdateCharacterRequest,
  NotificationType,
  useLoadCharacter,
  useLorebook,
  useTranslation,
  useUserInfo,
} from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import type { RulebookAttachmentChange } from "@next-platform/client-common-core/rulebook/attach/useRulebookAttachmentSelection";
import { Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { Lorebook } from "@next-platform/packages-ts-types";
import { Spinner } from "@nextui-org/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { PageLayout, PageSize, PageVariant } from "@/components";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import TextStyles from "@/components/common/TextStyles";
import { Grid } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_ROUTES_FOR_APPLICATION, FEATURE_FLAGS, getDraftStorageKey, ROUTES } from "@/constants";
import { useFeatureFlag } from "@/hooks";
import useToast from "@/hooks/useToast";
import CharacterForm from "@/pages/CreateCharacter/v2/components/CharacterForm";
import CreateCharacterHeroSection from "@/pages/CreateCharacter/v2/components/CreateCharacterHeroSection";
import CreateCharacterLockedOverlay from "@/pages/CreateCharacter/v2/components/CreateCharacterLockedOverlay";
import CreateCharacterPerksSection from "@/pages/CreateCharacter/v2/components/CreateCharacterPerksSection";
import ModerationStatus from "@/pages/CreateCharacter/v2/components/ModerationStatus";
import useAuth from "@/services/useAuth";

import { CHATBOT_SAVE_DEFERRED_FOR_MODERATION_CONFIRM } from "./constants";

const chatbotEditLogger = appLogger.withTag("ChatbotEdit");

export default function ChatbotEdit({ characterId }: { characterId: string }) {
  const [stateIsEditNotAccessible, setStateIsEditNotAccessible] = useState(true);
  const [stateIsSubmitForManualReview, setStateIsSubmitForManualReview] = useState(false);
  const [stateIsLorebookOperationInProgress, setStateIsLorebookOperationInProgress] = useState(false);
  const [stateIsShowModerationSubmitConfirm, setStateIsShowModerationSubmitConfirm] = useState(false);
  const [stateIsModerationConfirmLoading, setStateIsModerationConfirmLoading] = useState(false);
  const refPendingModerationSave = useRef<{
    /** Form payload before `submitForManualReview` enrichment — ref must stay raw so retries use current `stateIsSubmitForManualReview`. */
    rawProps: IUpdateCharacterRequest;
    currentLorebooks: Lorebook[];
    initialLorebooks: Lorebook[];
    rulebookChange?: RulebookAttachmentChange;
  } | null>(null);
  const { hasFeatureFlag: hasBotUpdateModeration } = useFeatureFlag(FEATURE_FLAGS.PUBLIC_BOT_EDIT_MODERATION);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { draftId } = useParams();
  const effectiveDraftId = draftId || characterId;
  const { userId } = useUserInfo();
  const { displayToast } = useToast();
  const draftKey = getDraftStorageKey(userId);
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const applicationId = basicAppConfig.applicationId;
  const {
    onUserUpdateCharacter,
    isLoading: isCharacterLoading,
    loadingType: characterLoadingType,
    errorMessage,
    characterData,
  } = useLoadCharacter({ characterId: characterId ?? "" });
  const { onUserAttachLorebook, onUserDetachLorebook } = useLorebook();
  const isPageLoading = (isCharacterLoading && characterLoadingType === CharacterFlow.GET_CHARACTER) || isAuthLoading;
  const character = characterData as ICharacter | null;
  const isFixRequired = character?.moderationReport?.moderation_status === CHARACTER_MODERATION_STATUS.FIX_REQUIRED;
  // Drafts are only ever written from the Action Required flow, so a draft found outside it is a
  // leftover from an abandoned session. Applying it would silently shadow the server data with a
  // stale copy the user cannot see or discard from this screen.
  const storedData = useMemo(() => {
    if (!isFixRequired) {
      return null;
    }

    try {
      return JSON.parse(localStorage.getItem(draftKey) as string)?.[effectiveDraftId as string] ?? null;
    } catch {
      return null;
    }
  }, [draftKey, effectiveDraftId, isFixRequired]);
  const isSubmitting =
    (isCharacterLoading && characterLoadingType === CharacterFlow.UPDATE_CHARACTER) ||
    stateIsLorebookOperationInProgress ||
    stateIsModerationConfirmLoading;
  useEffect(() => {
    if (character?.creator_user_id) {
      setStateIsEditNotAccessible(character?.creator_user_id !== userId);
    }
  }, [character?.creator_user_id, userId]);

  const executeSave = useCallback(
    async (
      editCharacterProps: IUpdateCharacterRequest,
      currentLorebooks: Lorebook[],
      initialLorebooks: Lorebook[],
      rulebookChange?: RulebookAttachmentChange,
    ) => {
      const { data: savedCharacter, error, errorPayload } = await onUserUpdateCharacter(characterId, editCharacterProps);

      if (error) {
        displayToast({ message: error, type: NotificationType.error });

        if (errorPayload?.data?.submitForManualReview === true) {
          setStateIsSubmitForManualReview(true);
        }
        // eslint-disable-next-line id-denylist -- property name matches ICharacterHandlerReturn
        return { data: savedCharacter, error };
      }

      if (!savedCharacter?.id) {
        // eslint-disable-next-line id-denylist -- property name matches ICharacterHandlerReturn
        return { data: savedCharacter, error };
      }

      const initialLorebookIds = initialLorebooks.map((lorebook) => lorebook.id);
      const currentLorebookIds = currentLorebooks.map((lorebook) => lorebook.id);
      const lorebooksToDetach = initialLorebookIds.filter((lorebookId) => !currentLorebookIds.includes(lorebookId));
      const lorebooksToAttach = currentLorebookIds.filter((lorebookId) => !initialLorebookIds.includes(lorebookId));
      const lorebookCharacterId = savedCharacter.character_id ?? savedCharacter.id;

      const hasRulebookChange = rulebookChange !== undefined && rulebookChange.initialRulebookId !== rulebookChange.currentRulebookId;
      if (lorebooksToDetach.length > 0 || lorebooksToAttach.length > 0 || hasRulebookChange) {
        setStateIsLorebookOperationInProgress(true);
      }

      try {
        const detachResults = await Promise.all(lorebooksToDetach.map((lorebookId) => onUserDetachLorebook(lorebookId, lorebookCharacterId)));
        const detachFailure = detachResults.find((result) => result?.error != null);
        if (detachFailure?.error != null) {
          throw new Error(`Failed to detach lorebook: ${detachFailure.error}`);
        }

        const attachResults = await Promise.all(
          lorebooksToAttach.map((lorebookId) => onUserAttachLorebook(lorebookId, { characterId: lorebookCharacterId })),
        );
        const attachFailure = attachResults.find((result) => result?.error != null);
        if (attachFailure?.error != null) {
          throw new Error(`Failed to attach lorebook: ${attachFailure.error}`);
        }

        if (rulebookChange && hasRulebookChange) {
          await applyRulebookAttachmentChange({ ownerType: "character", ownerId: lorebookCharacterId, ...rulebookChange }).catch(() => {
            throw new Error(t("chatbot:form.section.rulebook.updateFailed", "The Rulebook couldn't be updated. Try saving again."));
          });
        }

        displayToast({ message: t("chatbot:form.toast.chatbotSaved"), type: NotificationType.success });
        navigate(ROUTES.chatbots);
      } catch (lorebookError) {
        chatbotEditLogger.error("Error updating lorebooks:", lorebookError);
        displayToast({
          message: lorebookError instanceof Error ? lorebookError.message : "Failed to update lorebooks",
          type: NotificationType.error,
        });
        // Don't navigate away if lorebook update failed
        // eslint-disable-next-line id-denylist -- property name matches ICharacterHandlerReturn
        return { data: savedCharacter, error: "Lorebook update failed" };
      } finally {
        setStateIsLorebookOperationInProgress(false);
      }

      // eslint-disable-next-line id-denylist -- property name matches ICharacterHandlerReturn
      return { data: savedCharacter, error };
    },
    [characterId, displayToast, navigate, onUserAttachLorebook, onUserDetachLorebook, onUserUpdateCharacter, t],
  );

  const buildEditCharacterProps = useCallback(
    (props: IUpdateCharacterRequest) => (stateIsSubmitForManualReview ? { ...props, submitForManualReview: true } : props),
    [stateIsSubmitForManualReview],
  );

  const onSave = useCallback(
    async (props: IUpdateCharacterRequest, currentLorebooks: Lorebook[], initialLorebooks: Lorebook[], rulebookChange?: RulebookAttachmentChange) => {
      const editCharacterProps = buildEditCharacterProps(props);
      // TODO(jerome): [NX-8951] regroup logic in CharacterForm OR ChatbotEdit for the display of SubmitForReviewModal and ConfirmationModal
      const isPublicBot = character?.visibility === CHARACTER_VISIBILITY.PUBLIC;
      const isTargetIsPublic = editCharacterProps.visibility === CHARACTER_VISIBILITY.PUBLIC;
      // If moderation status is fix required, we dont need to show this modal because we already display SubmitForReviewModal
      const isFixRequired = character?.moderationReport?.moderation_status === CHARACTER_MODERATION_STATUS.FIX_REQUIRED;

      const shouldBypassReview = checkShouldBypassModerationReview({
        isEdit: true,
        isPublic: isPublicBot && isTargetIsPublic,
        hasUpdateModerationFlag: hasBotUpdateModeration,
        submitForManualReview: stateIsSubmitForManualReview,
        currentValues: props as Record<string, unknown>,
        initialValues: (character ?? {}) as Record<string, unknown>,
      });

      if (hasBotUpdateModeration && isPublicBot && isTargetIsPublic && !stateIsSubmitForManualReview && !isFixRequired && !shouldBypassReview) {
        refPendingModerationSave.current = {
          rawProps: props,
          currentLorebooks,
          initialLorebooks,
          rulebookChange,
        };
        setStateIsShowModerationSubmitConfirm(true);
        // Truthy `error` prevents CharacterForm from treating this as a successful save until the user confirms.
        return { error: CHATBOT_SAVE_DEFERRED_FOR_MODERATION_CONFIRM };
      }

      return executeSave(editCharacterProps, currentLorebooks, initialLorebooks, rulebookChange);
    },
    [buildEditCharacterProps, executeSave, hasBotUpdateModeration, stateIsSubmitForManualReview, character],
  );

  const handleModerationSubmitConfirmClose = () => {
    setStateIsShowModerationSubmitConfirm(false);
    refPendingModerationSave.current = null;
  };

  const handleModerationSubmitConfirm = useCallback(async (): Promise<void | { error: string }> => {
    const pending = refPendingModerationSave.current;
    if (!pending) {
      return;
    }

    setStateIsModerationConfirmLoading(true);
    try {
      const editCharacterProps = buildEditCharacterProps(pending.rawProps);
      const result = await executeSave(editCharacterProps, pending.currentLorebooks, pending.initialLorebooks, pending.rulebookChange);
      if (result?.error) {
        const message = typeof result.error === "string" ? result.error : "Something went wrong while saving.";
        return { error: message };
      }
      refPendingModerationSave.current = null;
    } finally {
      setStateIsModerationConfirmLoading(false);
    }
  }, [buildEditCharacterProps, executeSave]);

  if (isPageLoading) {
    return <Spinner className={TextStyles.spinner} />;
  }

  return (
    <PageLayout size={PageSize.Medium} variant={PageVariant.Modal}>
      <PageStaticMetaTags pageId="editCharacter" properties={{ characterName: character?.name }} data-testid="ChatbotEdit-PageStaticMetaTags" />
      {!isAuthenticated && (
        <>
          <CreateCharacterHeroSection />
          <CreateCharacterPerksSection />
        </>
      )}
      <div className="flex flex-col">
        {!isAuthenticated ? <CreateCharacterLockedOverlay /> : null}
        <Grid className="flex justify-between flex-col mob:flex-row">
          <Text variant={TextVariants.HEADING_5_BOLD}>
            <Trans i18nKey="chatbot:editChatbot.title" />
          </Text>
          <LocalizedLink
            className="flex items-center text-link"
            target="_blank"
            to={EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.create_chatbot_docs?.creation_guide}
            aria-label="chatbot-guide"
          >
            <Text variant={TextVariants.LABEL_MEDIUM_REGULAR}>
              <Trans i18nKey="chatbot:editChatbot.creationGuide" />
            </Text>
          </LocalizedLink>
        </Grid>
        <ModerationStatus
          applicationId={applicationId as string}
          moderationReasonCodes={character?.moderationReport?.moderation_reason_codes}
          actionTakenDescription={character?.moderationReport?.action_taken_description}
          reasonForModeration={character?.moderationReport?.reason_for_moderation}
          moderationStatus={character?.moderationReport?.moderation_status as CHARACTER_MODERATION_STATUS}
          reportsType={character?.reportsType}
          type={character?.type}
        />
        {characterId && stateIsEditNotAccessible ? (
          <Text>
            <Trans i18nKey="chatbot:editChatbot.permissionDenied" />
          </Text>
        ) : null}
        {isPageLoading && !character && !storedData ? <Spinner /> : null}
        {(!isPageLoading || storedData || character) && !stateIsEditNotAccessible ? (
          <CharacterForm
            {...(storedData ? { ...character, ...storedData } : character)}
            groupAddable={(storedData ? storedData?.group_addable : character?.group_addable) ?? true}
            onSave={onSave}
            onCancel={() => navigate("/")}
            // In the old ChatbotEdit page the allowImport is false
            allowImport={false}
            draftId={storedData ? effectiveDraftId : undefined}
            setIsSubmitForManualReview={setStateIsSubmitForManualReview}
            isSubmitting={isSubmitting}
            submitError={errorMessage}
            submitForManualReview={stateIsSubmitForManualReview}
            isEdit={true}
            isAlreadyDraft={Boolean(storedData)}
            handleModerationSubmitConfirmClose={handleModerationSubmitConfirmClose}
            stateShowModerationSubmitConfirm={stateIsShowModerationSubmitConfirm}
            stateModerationConfirmLoading={stateIsModerationConfirmLoading}
            handleModerationSubmitConfirm={handleModerationSubmitConfirm}
          />
        ) : null}
      </div>
    </PageLayout>
  );
}
